<template>
  <div id="slingshot-app" v-bind:class="{ 'blue-background': blueBackground }">
    <div>
      <!-- Admin settings / Help -->
      <transition name="slide">
        <div v-if="help || settings" id="settings-help">
          <div class="info-header">
            <div></div>
            <div>
              <h2>{{ settings ? 'SETTINGS' : 'HELP' }}</h2>
            </div>
            <div>
              <img :src="require(`../assets/close-icon.svg`)" @click="close" class="icon-button" />
            </div>
          </div>
          <div class="content">
            <div class="help-content">
              <div v-if="settings">
                <a :href="messagesUrl + '/settings/' + this.organizationCode + '.com/'" target="_blank">
                  <p>Account setup</p>
                </a>
              </div>
              <div v-else>
                <a href="https://help.unbabel.com/hc/en-us/sections/360009044214-Unbabel-for-Freshdesk" target="_blank">
                  <p>FAQs</p>
                </a>
              </div>
              <hr/>
            </div>
            <div class="help-footer">
              <h3>REMEMBER</h3>
              <p class="info">Translate a message by typing #unbabel before replying</p>
            </div>
          </div>
        </div>
      </transition>

      <!-- unbabel token not set in freshdesk client db  -->
      <div v-if="status === 'login'" id="no-token-panel" class="content">
        <OnboardingScreen
          :payload="payload"
          @updatePayload="payload = $event"
          @complete="load"
        />
      </div>

      <!-- Loading -->
      <div v-else-if="status === 'loading'" class="loading-panel">
        <div>
          <Loader :is-global="false" :active="true"></Loader>
        </div>
        <p>Loading</p>
      </div>

      <!-- App -->
      <div v-else> <!-- logged in -->
        <div class="info-header">
          <!-- Cog icon for admin settings -->
          <div>
            <img
              v-if="agent"
              :src="require(`../assets/cog-icon-${iconColor}.svg`)"
              class="icon-button"
              @click="showSettings"
            />
          </div>
          <div>
            <h2 v-bind:class="{ 'white-text': blueBackground }">{{ headerText }}</h2>
          </div>
          <!-- Info icon for more information -->
          <div>
            <img :src="require(`../assets/i-icon-${iconColor}.svg`)" @click="showHelp" class="icon-button"
/>
          </div>
        </div>

        <!-- "Start unbabel" view -->
        <div v-if="status === 'ticket_not_found'" class="content">
          <div class="status-content">
            <div class="no-box">
              <h2 class="blue-text">Looking for translations?</h2>
              <p class="info dark-text">
                Your account settings prevent automatic translation. Click below to request.
              </p>
            </div>
          </div>
          <div class="action-content">
            <Button
              title="TRANSLATE"
              label="TRANSLATE"
              @click="startUnbabel(true)"
            />
          </div>
        </div>

        <!-- invalid or not found api key -->
        <div v-else-if="status === 'no_api_key'" id="no-api-key" class="content">
          <div class="shorter-status-content">
            <div class="no-box white-text">
              <h2>Enter your Freshdesk API key to get started.</h2>
              <p class="info">
                <a :href="`https://${organizationCode}.com/a/profiles/${agent.code}/edit`" target="_blank">
                  What's my key?!
                </a>
              </p>
            </div>
            <div>
              <img :src="require('../assets/square.png')"/>
            </div>
          </div>
          <div class="action-content">
            <InputText
              label="API Key"
              :data="apiKeyInput"
              @change="updateInputData('apiKeyInput', $event)"
            />
            <Button
              title="ENTER"
              label="ENTER"
              button-style="is-primary-inverted"
              :disabled="!apiKeyInput"
              @click="submitApiKey"
            />
          </div>
        </div>

        <!-- Agent not authorized -->
        <div v-else-if="status === 'agent_unauthorized'" id="unauthorized" class="content">
          <div class="shorter-status-content">
            <div class="no-box white-text">
              <h2>Trying to use Unbabel?</h2>
              <p class="info">
                Unbabel is not enabled for you. Once your Admin activates your account, you can start speaking to your customers in their native language.
              </p>
            </div>
            <div>
              <img :src="require('../assets/zigzag.png')"/>
            </div>
            <div>
              <img :src="require('../assets/triangle.png')"/>
            </div>
          </div>
        </div>

        <!-- TODO: Refactor with a component for these cases -->
        <!-- Translating / Translated / Same Language / No subscription / Failed to deliver -->
        <div v-else class="content">
          <img class="top-status-image" :src="require(`../assets/image-${imageColor}.svg`)"/>
          <!-- Translating -->
          <div v-if="status === 'translating'" class="status-content">
            <div class="box-container">
              <div class="box white-box">
                <h3>TRANSLATION IN PROGRESS</h3>
                <p v-if="lastTranslation.mode === 'human'">Our community of editors is working hard on this. Check back soon for progress.</p>
                <p v-else>Our AI engines are working hard on this.</p>
              </div>
            </div>
          </div>
          <!-- Translated -->
          <div v-else-if="status === 'delivered'" class="status-content">
            <div class="box-container">
              <div class="box green-box">
                <h3>TRANSLATION COMPLETE</h3>
                <p v-if="lastMessage.inbound">Check the ticket for the translated message.</p>
                <p v-else>The customer has received your message.</p>
              </div>
            </div>
            <div class="upgrade-button">
              <Button
                v-if="lastTranslation.mode === 'mt'"
                title="UPGRADE"
                label="UPGRADE"
                button-type="is-outlined"
                @click="upgradeLastMachineTranslation"
              />
            </div>
          </div>
          <!-- Same Language -->
          <div v-else-if="['customer_language_skipped', 'same_language'].includes(status)" class="status-content">
            <div class="box-container">
              <div class="box blue-box">
                <h3>NO TRANSLATION NEEDED</h3>
                <p v-if="agent">You added this as a language you know. You can change it in Settings.</p>
                <p v-else>This was added as a language you know. Contact your admin to change it.</p>
              </div>
            </div>
          </div>
          <!-- Unsupported Language / No subscription -->
          <div v-else-if="['no_subscription', 'language_workflow_not_found'].includes(status)" class="status-content">
            <div class="box-container">
              <div class="box orange-box">
                <h3>UNSUPPORTED LANGUAGE</h3>
                <p>
                  {{ `${customerLanguageInput[0].toUpperCase()}${customerLanguageInput.substr(1)}` }} translation is currently not enabled. Contact your Admin to add new languages.
                </p>
              </div>
            </div>
          </div>
          <!-- Translation Error -->
          <div v-else class="status-content">
            <div class="box-container">
              <div class="box red-box">
                <h3>TRANSLATION ERROR</h3>
                <p>{{error.description}}</p>
                <br>
                <p>If problems persist, contact your Admin</p>
              </div>
            </div>
          </div>

          <div v-if="customerLanguageInput" class="action-content">
            <p v-bind:class="{ 'white-text': blueBackground }">CUSTOMER LANGUAGE</p>
            <select @change="changeCustomerLanguage" v-model="customerLanguageInput">
              <option :value="language.name" v-for="language in languages" :key="language.name">{{ language.name }}</option>
            </select>
          </div>
        </div>

      </div>
    </div>
    <resize-sensor @resize="resizeApplet"></resize-sensor>

  </div>
</template>

<script>
import resizeSensor from 'vue-resize-sensor'
import { mapActions, mapGetters } from 'vuex'
import { Button, InputText, Loader } from '@unbabel/ui'
import OnboardingScreen from '../components/OnboardingScreen.vue'

export default {
  name: 'Slingshot',
  props: ['payload'],
  components: {
    Button,
    InputText,
    Loader,
    resizeSensor,
    OnboardingScreen
  },
  data: function () {
    return {
      status: 'loading',
      organizationCode: '',
      agentEmail: '',
      agent: {},
      ticket: {},
      lastMessage: {},
      lastTranslation: {},
      error: {},
      languages: window.LANGUAGES.sort((a, b) => a.name.localeCompare(b.name)),
      apiKeyInput: '',
      customerLanguageInput: '',
      settings: false,
      help: false,
      messagesUrl: window.MESSAGES_ENDPOINT
    }
  },
  mounted () {
    this.load()
  },
  computed: {
    ...mapGetters([
      'api',
      'settings',
      'clientController'
    ]),
    languagesOptions: function () {
      return this.languages.map(language => {
        return {label: language.name, value: language.name}
      })
    },
    headerText: function () {
      if (this.status === 'no_api_key') {
        return 'SETUP'
      } else if (this.settings) {
        return 'SETTINGS'
      } else if (this.help) {
        return 'HELP'
      } else if (this.lastMessage.inbound) {
        return 'INBOUND'
      } else if (this.lastMessage.inbound === false) {
        return 'OUTBOUND'
      }
      return ''
    },
    blueBackground: function () {
      return ['no_api_key', 'agent_unauthorized', 'translating', 'login'].includes(this.status)
    },
    iconColor: function () {
      if (this.blueBackground) return 'white'
      return 'grey'
    },
    imageColor: function () {
      if (this.status === 'translating') {
        return this.lastMessage.inbound ? 'inbound' : 'outbound'
      } else if (['no_subscription', 'language_workflow_not_found'].includes(this.status)) {
        return 'orange'
      } else if (this.status === 'delivered') {
        return 'green'
      } else if (['customer_language_skipped', 'same_language'].includes(this.status)) {
        return 'blue'
      }
      return 'red'
    },
    slideClass: function () {
      return this.settings || this.help ? 'slide-in' : 'slide-out'
    }
  },
  methods: {
    ...mapActions([
      'fetchOrganizationCode',
      'fetchClientAgent',
      'resizeApplet'
    ]),
    async load () {
      try {
        this.organizationCode = await this.fetchOrganizationCode()

        const { agentCode, agentEmail } = await this.fetchClientAgent()
        this.agentEmail = agentEmail
        this.agent = await this.api.fetchAgent(agentCode, this.organizationCode)

        this.clientController.onAddNote = () => {
          const addHashtag = this.agent.organization.settings.add_trigger_word
          const ticketInitialized = Boolean(this.lastTranslation && Object.keys(this.lastTranslation).length)
          if (addHashtag && ticketInitialized) {
            this.clientController.prependTextToNote('#unbabel</br></br>')
          }
        }

        const startAutomatically = this.agent.organization.settings.start_automatically
        this.startUnbabel(startAutomatically)
      } catch (err) {
        if (err.status === 401 || err.status === 403) {
          this.status = 'login'
        }
      }
    },
    async startUnbabel (forceInit) {
      this.status = 'loading'
      if (!this.agent.data.freshdeskKey) {
        this.status = 'no_api_key'
        return
      }
      if (!this.agent.authorized) {
        this.status = 'agent_unauthorized'
        return
      }

      let ticketStatus
      try {
        ticketStatus = await this.api.getOrRequestTicketAPITranslation(
          {
            ...this.payload,
            agentEmail: this.agentEmail
          },
          Boolean(forceInit)
        )
      } catch (error) {
        ticketStatus = null
        this.error = { description: error }
      }
      this.handleTicketStatusResponse(ticketStatus)
    },
    async changeCustomerLanguage () {
      this.status = 'loading'
      let ticketStatus
      try {
        ticketStatus = await this.api.changeCustomerLanguage(
          this.agent.organization.id,
          this.payload.threadCode,
          this.getLanguageCodeByName(this.customerLanguageInput),
          this.agent.code,
          this.agent.preferred_language,
          this.agent.languages_spoken
        )
      } catch (error) {
        ticketStatus = null
        this.error = { description: error }
      }
      this.handleTicketStatusResponse(ticketStatus)
    },
    async upgradeLastMachineTranslation () {
      this.status = 'loading'
      let ticketStatus
      try {
        ticketStatus = await this.api.upgradeLastMachineTranslation(
          this.agent.organization.id,
          this.ticket.external_id
        )
      } catch (error) {
        ticketStatus = null
        this.error = { description: error }
      }
      this.handleTicketStatusResponse(ticketStatus)
    },
    async submitApiKey () {
      this.status = 'loading'
      await this.api.updateAgent(
        this.agent.id,
        { data: {...this.agent.data, freshdeskKey: this.apiKeyInput} }
      )
      this.agent = await this.api.fetchAgent(this.agent.code, this.organizationCode)
      const forceCreateTicket = true
      this.startUnbabel(forceCreateTicket)
    },
    handleTicketStatusResponse (ticketStatus) {
      // if not ticketStatus fallback to old app
      if (!ticketStatus) {
        this.status = 'error'
        return
      }

      const data = ticketStatus.data
      if (data) {
        this.ticket = data.ticket
        this.lastMessage = data.message
        this.lastTranslation = data.translation
        this.customerLanguageInput = this.getLanguageNameByCode(
          data.ticket.customer_language_id
        )
      }
      if (['created', 'upgrade_requested'].includes(ticketStatus.end_state)) {
        this.status = this.lastTranslation.state
      } else if (['customer_language_skipped', 'same_language', 'no_subscription', 'language_workflow_not_found'].includes(ticketStatus.end_state)) {
        this.error = ticketStatus.error
        this.status = ticketStatus.end_state
        this.customerLanguageInput = (
          this.getLanguageNameByCode(
            this.error.details[0]['source_language_id'] || this.error.details[0]['customer_language_id']
          )
        )
      } else if (ticketStatus.end_state === 'ok') {
        const translationState = this.lastTranslation.state
        if (['created', 'translating', 'translated'].includes(translationState)) {
          this.status = 'translating'
        } else {
          this.status = translationState
        }
      } else {
        this.status = ticketStatus.end_state
      }
    },
    getLanguageNameByCode (languageCode) {
      return this.languages.find(
        language => language.code === languageCode
      ).name
    },
    getLanguageCodeByName (languageName) {
      return this.languages.find(
        language => language.name === languageName
      ).code
    },
    updateInputData: function (fieldName, value) {
      this[fieldName] = value
    },
    showSettings () {
      this.settings = true
    },
    showHelp () {
      this.help = true
    },
    close () {
      this.help = false
      this.settings = false
    }
  }
}
</script>

<style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@600&display=swap");

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    text-align: center;
    font-family: "Montserrat";
    font-weight: 600;
  }

  #slingshot-app {
    height: 400px;
    --blue: #3843D0;
    --dark-grey: #343941;
    --grey: #59616E;
    --light-grey: #EBEFF5;
  }

  #slingshot-app select {
    max-width: 170px;
    height: 23px;
    border: 0px;
    border-radius: 4px;
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    background-color: var(--light-grey)
  }

  a, a:visited {
    color: white;
  }

  hr {
    color: #DEE3ED;
  }

  .blue-text {
    color: var(--blue);
  }

  .white-text {
    color: var(--light-grey);
  }

  .dark-text {
    color: var(--dark-grey)
  }

  .blue-background {
    background-color: var(--blue)
  }

  /*  */
  /* Samora components custom changes */
  /*  */
  /* Using id selectors to override frontdesk component css rules */
  #slingshot-app .c-Button {
    width: 190px;
    height: 36px;
    justify-content: center;
  }

  #slingshot-app .action-content .c-inputText {
    width: 190px;
    height: 48px;
    margin-bottom: 8px;
  }

  /*  */
  /* Loading Panel */
  /*  */
  .loading-panel {
    margin-top: 100px;
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
  }

  .loading-panel > p {
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
    margin-top: 25px;
  }

  /*  */
  /* Header */
  /*  */
  .info-header {
    height: 45px;
    display: flex;
    align-items: center;
  }

  .info-header h2 {
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
    color: var(grey);
  }

  .info-header > div {
    flex-grow: 1;
    flex-basis: 42px;
  }

  .info-header > div:nth-child(2) {
    flex-grow: 4;
  }

  /*  */
  /* Settings / Help  */
  /*  */
  #settings-help {
    position: absolute;
    background: white;
    height: 400px;
    z-index: 1;
  }

  #settings-help .help-content {
    height: 70%;
    padding: 6px 0 0 18px;
    text-align: left;
  }

  .help-content > div {
    height: 40px;
  }

  .help-content p {
    padding: 5px 0px;
    font-family: Open Sans;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.25px;
    color: #3843D0;
    text-align: left;
  }

  #settings-help .help-footer {
    padding-top: 16px;
    height: 30%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: #FFF8E1;
  }

  .help-footer > * {
    text-align: left !important;
    padding: 6px 18px 18px;
  }

  .slide-enter-active {
    animation: slide-in 0.5s forwards;
  }

  .slide-leave-active {
    animation: slide-out 0.5s forwards;
  }

  @keyframes slide-in {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(0%); }
  }

  @keyframes slide-out {
    0% { transform: translateY(0%); }
    100% { transform: translateY(-100%); }
  }

  /* Content - general styles */
  .content {
    height: 355px;
  }

  .content h2 {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.15px;
    margin-bottom: 18px;
  }

  .content h3 {
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
    padding-bottom: 8px;
    color: unset; /* override old app css rule */
  }

  /*  */
  /* Status content / Status information */
  /*  */
  .top-status-image {
    position: absolute;
    top: 45px;
    left: 83px;
  }

  .status-content {
    height: 75%;
  }

  .shorter-status-content {
    height: 65%;
  }

  .status-content > .upgrade-button {
    display: flex;
    justify-content: center;
  }

  .box-container {
    padding: 30px 18px 12px;
  }

  .box {
    padding: 47px 20px 30px;
    border-radius: 4px;
  }

  .no-box {
    padding: 38px 18px 0;
  }

  .box > p {
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.1px;
    color: var(--dark-grey);
  }

  .white-box {
    background-color: white;
    color: var(--dark-grey);
  }

  .green-box {
    background-color: #F1F8E9;
    color: #33691E;
  }

  .blue-box {
    background-color: #E9EAFB;
    color: #1111A6;
  }

  .orange-box {
    background-color: #FFF8E1;
    color: #1111A6;
  }

  .red-box {
    background-color: #FFEBEE;
    color: #FF6F00;
  }

  .info {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    letter-spacing: 0.4px;
  }

  #no-api-key .shorter-status-content div:nth-child(2) {
    padding: 35px 0 0 140px;
  }

  #unauthorized .shorter-status-content > div:nth-child(2) {
    padding: 35px 140px 0 0;
  }

  #unauthorized .shorter-status-content > div:last-child {
    padding: 10px 0 0 140px;
  }

  /*  */
  /* Actionable content / footer */
  /*  */
  .action-content {
    height: 25%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    color: var(--grey)
  }

  .action-content p {
    padding-bottom: 8px;
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 1.5px;
  }

  .icon-button {
    cursor: pointer;
  }

</style>



// WEBPACK FOOTER //
// src/components/Slingshot.vue